import { useCallback, useLayoutEffect, useState, type RefObject } from 'react'

export interface PopoverPosition {
  left: number
  width: number
  top?: number
  bottom?: number
  maxHeight: number
}

/** Anything with a viewport rect: an element, or a point (a context menu's pointer). */
export interface PopoverAnchor {
  getBoundingClientRect: () => DOMRect
}

export interface PopoverPlacementRefs {
  /** The element (or point) the popover is anchored to. */
  trigger: RefObject<PopoverAnchor | null>
  /** The floating panel itself — its rendered height becomes the "chrome" budget. */
  panel: RefObject<HTMLElement | null>
  /** The panel's scrollable region (its clipped height is measured against `content`). */
  scrollArea: RefObject<HTMLElement | null>
  /** The scroll area's child holding the actual rows — `scrollHeight` is its natural height. */
  content: RefObject<HTMLElement | null>
}

/** Which trigger edge a popover lines up with. */
export type PopoverAlign = 'start' | 'end'

export interface UsePopoverPlacementOptions {
  minWidth?: number
  /**
   * Which trigger edge the panel lines up with. `start` (default) shares the
   * left edge, as a field's list does; `end` shares the right edge, as a「…」
   * menu at the end of a header does.
   */
  align?: PopoverAlign
  /**
   * The panel's own CSS width (a token) is the width, instead of the trigger's
   * width floored at `minWidth`. The returned `width` is the measured one, for
   * clamping only — the caller does not set it inline.
   */
  sizeToPanel?: boolean
}

const DEFAULT_MIN_WIDTH = 200
const VIEWPORT_GAP = 8
const GAP = 6

/**
 * Measuring the same geometry twice must not publish a new object: every new
 * identity re-renders each consumer, and a caller whose `recalcDeps` carry a
 * ReactNode re-measures on every parent render. A burst of those nested inside
 * a layout effect is what trips React's "Maximum update depth exceeded" guard.
 */
function samePosition(a: PopoverPosition | null, b: PopoverPosition): boolean {
  return (
    a !== null &&
    a.left === b.left &&
    a.width === b.width &&
    a.top === b.top &&
    a.bottom === b.bottom &&
    a.maxHeight === b.maxHeight
  )
}

/**
 * Measures a trigger + floating panel pair and returns viewport-fixed
 * coordinates, flipping the panel above the trigger when the space below
 * cannot fit its measured content. `position: fixed` (not `absolute`) is
 * required because both `Picker` and `Dropdown` mount inside panels that
 * scroll — an absolutely-positioned menu would be clipped by that ancestor's
 * overflow.
 *
 * Returns `[position, resetPosition]`; the caller resets on close so a stale
 * position never flashes on the next open before the first measurement.
 */
export function usePopoverPlacement(
  open: boolean,
  refs: PopoverPlacementRefs,
  recalcDeps: readonly unknown[] = [],
  options: UsePopoverPlacementOptions = {}
): [PopoverPosition | null, () => void] {
  const [position, setPosition] = useState<PopoverPosition | null>(null)
  const minWidth = options.minWidth ?? DEFAULT_MIN_WIDTH
  const align = options.align ?? 'start'
  const sizeToPanel = options.sizeToPanel ?? false
  const resetPosition = useCallback(() => setPosition(null), [])

  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const trigger = refs.trigger.current
      if (!trigger) return
      const rect = trigger.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const panel = refs.panel.current
      const naturalWidth = sizeToPanel
        ? (panel?.getBoundingClientRect().width ?? 0)
        : Math.max(rect.width, minWidth)
      const width = Math.min(naturalWidth, viewportWidth - VIEWPORT_GAP * 2)
      const preferredLeft = align === 'end' ? rect.right - width : rect.left
      const left = Math.max(
        VIEWPORT_GAP,
        Math.min(preferredLeft, viewportWidth - VIEWPORT_GAP - width)
      )
      const scrollArea = refs.scrollArea.current
      const content = refs.content.current
      // Measure the rendered frame (header/footer/padding/border) rather than
      // using a magic flip threshold. The available side gets any remaining
      // height as the scroll area's own height.
      const panelHeight = panel?.getBoundingClientRect().height ?? 0
      const scrollAreaHeight = scrollArea?.getBoundingClientRect().height ?? 0
      const chromeHeight = Math.max(0, panelHeight - scrollAreaHeight)
      const contentHeight = content?.scrollHeight ?? 0
      const below = Math.max(0, viewportHeight - rect.bottom - GAP - VIEWPORT_GAP)
      const above = Math.max(0, rect.top - GAP - VIEWPORT_GAP)
      const fitsBelow = below >= chromeHeight + contentHeight
      const next: PopoverPosition =
        !fitsBelow && above > below
          ? { left, width, bottom: viewportHeight - rect.top + GAP, maxHeight: above }
          : { left, width, top: rect.bottom + GAP, maxHeight: below }
      setPosition((current) => (samePosition(current, next) ? current : next))
    }
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    // The panel's own content is the authoritative "shape changed" signal, so
    // callers do not have to push a fresh ReactNode through `recalcDeps` (whose
    // new identity on every parent render re-ran this effect needlessly) to get
    // a re-measure when a row, an alert or a hint appears.
    const content = refs.content.current
    let observer: ResizeObserver | null = null
    if (content && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => place())
      observer.observe(content)
    }
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
      observer?.disconnect()
    }
    // `refs` is a stable object of refs and `minWidth` rarely changes;
    // `recalcDeps` is the caller's explicit "content shape changed" signal for
    // anything the ResizeObserver above cannot see.
  }, [open, minWidth, align, sizeToPanel, ...recalcDeps])

  return [position, resetPosition]
}
